import React from "react";
import { CommentReference, rng, StreamComment, UserToken } from "../../amorg-common";
import { createComment, deleteComment, getComments, updateComment } from "../../api/commentClientApi";
import { commentValidation } from "../../amorg-common/modules/fieldValidation/fields/commentValidation";
import { LoadingPageOverlay } from "../LoadingPageOverlay";
import clone from "clone";
import { BaseMessagePanel } from "./BaseMessagePanel";

export type CommentPanelProps = {
    readOnly?: boolean,
    commentReference: CommentReference,
    activeUser: UserToken,
    disableNewComment?: boolean,
    hide?: boolean,
    maxHeightPx?: number,
    onGetCommentCount?: (count: number) => void;
};

export function CommentPanel (props: CommentPanelProps): JSX.Element 
{
    const [loading, setLoading] = React.useState<boolean>(false);
    const [comments, setComments] = React.useState<StreamComment[]>([]);

    React.useEffect(() =>
    {
        getComments(props.commentReference.type, props.commentReference.id)
            .then(comments =>
            {
                setComments(comments);
                props.onGetCommentCount?.(comments.filter(comment => !comment.deleted).length);
            });
    }, [props.commentReference.type, props.commentReference.id]);

    async function handleClickAddComment (newComment: string): Promise<{ errorMessage?: string; }>
    {
        if (commentValidation.content(newComment))
        {
            setLoading(true);
            const result = await createComment(rng.getRandomId(), props.commentReference, newComment);
            if (result.isSuccess)
            {
                const updatedComments = await getComments(props.commentReference.type, props.commentReference.id);
                setComments(updatedComments);
                setLoading(false);
                props.onGetCommentCount?.(updatedComments.filter(comment => !comment.deleted).length);
                return { errorMessage: undefined };
            }
            else
            {
                setLoading(false);
                return { errorMessage: 'Der opstod en fejl. Prøv igen eller kontakt support' };
            }
        }
        else
        {
            return { errorMessage: getDefaultCommentErrorMessage(newComment) };
        }
    }

    async function handleClickEditComment (id: string, comment: string): Promise<{ errorMessage?: string; }>
    {
        if (commentValidation.content(comment))
        {
            setLoading(true);
            const result = await updateComment(id, comment);
            if (result.isSuccess)
            {
                const updatedComments = await getComments(props.commentReference.type, props.commentReference.id);
                setComments(updatedComments);
                setLoading(false);
                props.onGetCommentCount?.(updatedComments.filter(comment => !comment.deleted).length);
                return { errorMessage: undefined };
            }
            else
            {
                setLoading(false);
                return { errorMessage: 'Der opstod en fejl. Prøv igen eller kontakt support' };
            }
        }
        else
        {
            return { errorMessage: getDefaultCommentErrorMessage(comment) };
        }
    }

    async function handleClickDeleteComment (id: string): Promise<{ errorMessage?: string; }>
    {
        setLoading(true);
        await deleteComment(id);
        const updatedComments = await getComments(props.commentReference.type, props.commentReference.id);
        setComments(updatedComments);
        props.onGetCommentCount?.(updatedComments.filter(comment => !comment.deleted).length);
        setLoading(false);
        return { errorMessage: undefined };
    }

    if (props.hide)
        return <></>;

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            <BaseCommentPanel
                readOnly={props.readOnly}
                comments={comments}
                activeUser={props.activeUser}
                disableNewComment={props.disableNewComment}
                maxHeightPx={props.maxHeightPx}
                onCreate={handleClickAddComment}
                onEdit={handleClickEditComment}
                onDelete={handleClickDeleteComment}
            />
        </>
    );
}


export type BaseCommentPanelProps = {
    readOnly?: boolean,
    activeUser: { userId:string },
    comments: {
        id: string,
        content: string,
        createdBy: { userId: string, displayName: string; },
        created: Date,
        updated: Date | null,
        deleted: boolean;
    }[],
    disableNewComment?: boolean,
    maxHeightPx?: number,
    commentValidator?: (content: string) => { errorMessage?: string; },
    onCreate: (content: string) => Promise<{ errorMessage?: string; }>,
    onEdit: (id: string, content: string) => Promise<{ errorMessage?: string; }>,
    onDelete: (id: string) => Promise<{ errorMessage?: string; }>;
};

export function BaseCommentPanel (props: BaseCommentPanelProps): JSX.Element 
{
    async function handleClickAddComment (content: string): Promise<{ errorMessage?: string; }>
    {
        const result = await props.onCreate(content);
        return result;
    }

    async function handleClickEditComment (id: string, content: string): Promise<{ errorMessage?: string; }>
    {
        const result = await props.onEdit(id, content);
        return result;
    }

    const sortedComments = React.useMemo(() =>
    {
        const commentsCopy = clone(props.comments);
        commentsCopy.sort((a, b) =>
        {
            return b.created.getTime() - a.created.getTime();
        });
        return commentsCopy;
    }, [props.comments]);

    return (
        <BaseMessagePanel
            messages={sortedComments.map(c => ({
                id: c.id,
                content: c.content,
                createdBy: c.createdBy,
                created: c.created,
                updated: c.updated,
                deleted: c.deleted,
                onEdit: !props.readOnly && c.createdBy.userId === props.activeUser.userId && !c.deleted
                    ? (id:string, content: string) => handleClickEditComment(id, content)
                    : undefined,
                onDelete: !props.readOnly && c.createdBy.userId === props.activeUser.userId && !c.deleted
                    ? () => props.onDelete(c.id)
                    : undefined,
            }))}
            onCreate={props.readOnly || props.disableNewComment ? undefined : (content: string) => handleClickAddComment(content)}
            inlineCreate
            maxHeightPx={props.maxHeightPx}
        />
    );
}

function getDefaultCommentErrorMessage (content: string): string
{
    return `Ugyldig kommentar. Skal udfyldes. Maks 5000 (${content.length}) tegn.`;
}